import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles, Egg, Shirt, Wrench, Footprints, Users2, Star, CheckCircle2, ArrowRight, Camera
} from "lucide-react";
import { PageHero } from "@/pages/About";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/pages/ImpactHub";

const SECTOR_META = {
    detergent: { icon: Sparkles, label: "Detergent Manufacturing", accent: "from-emerald-500/20 to-teal-600/20", ring: "ring-emerald-500/30" },
    poultry: { icon: Egg, label: "Poultry Production", accent: "from-amber-500/20 to-orange-600/20", ring: "ring-amber-500/30" },
    garments: { icon: Shirt, label: "Clothing and Garment Production", accent: "from-fuchsia-500/20 to-pink-600/20", ring: "ring-fuchsia-500/30" },
    welding: { icon: Wrench, label: "Welding and Metal Fabrication", accent: "from-slate-500/20 to-zinc-700/25", ring: "ring-slate-500/30" },
    footwear: { icon: Footprints, label: "Footwear Manufacturing", accent: "from-indigo-500/20 to-violet-600/20", ring: "ring-indigo-500/30" },
    community: { icon: Users2, label: "Community Enterprise", accent: "from-[color:var(--gsf-green-700)]/20 to-[color:var(--gsf-gold-500)]/20", ring: "ring-[color:var(--gsf-green-700)]/30" },
};

// Enterprise-supported sector infographic (fixed list per Foundation brief).
const SECTORS = [
    { key: "detergent", label: "Detergent Manufacturing" },
    { key: "poultry", label: "Poultry Production" },
    { key: "garments", label: "Clothing and Garment Production" },
    { key: "welding", label: "Welding and Metal Fabrication" },
    { key: "footwear", label: "Footwear Manufacturing" },
];

function SectorPlaceholder({ sectorKey, name }) {
    const meta = SECTOR_META[sectorKey] || SECTOR_META.community;
    const Icon = meta.icon;
    return (
        <div
            className={`w-full aspect-[4/3] rounded-2xl bg-gradient-to-br ${meta.accent} ring-1 ${meta.ring} flex items-center justify-center relative overflow-hidden`}
            aria-label={`${name} — sector illustration placeholder`}
            data-testid={`enterprise-placeholder-${sectorKey}`}
        >
            <div className="absolute inset-0 opacity-10 [background-image:radial-gradient(circle_at_20%_30%,white_0%,transparent_45%),radial-gradient(circle_at_80%_70%,white_0%,transparent_40%)]" />
            <div className="text-center px-6">
                <Icon size={44} strokeWidth={1.4} className="mx-auto text-[color:var(--gsf-green-700)]" />
                <div className="mt-3 gsf-eyebrow text-[color:var(--gsf-green-700)]">{meta.label}</div>
                <div className="mt-1 text-xs italic text-gray-600">Images coming soon.</div>
            </div>
        </div>
    );
}

function EnterpriseCard({ e }) {
    const meta = SECTOR_META[e.sector_key] || SECTOR_META.community;
    const Icon = meta.icon;
    const hasPhotos = (e.images || []).length > 0;
    const testid = `enterprise-card-${e.slug}`;
    return (
        <article className="gsf-card p-5 flex flex-col" data-testid={testid}>
            <div>
                {hasPhotos ? (
                    <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden relative bg-[color:var(--gsf-soft-green)]">
                        <img
                            src={resolveMediaUrl(e.images[0])}
                            alt={`${e.name} — ${e.sector}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            data-testid={`enterprise-img-${e.slug}`}
                        />
                        {e.featured && (
                            <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--gsf-gold-500)] text-black font-semibold">
                                <Star size={10} /> Featured Success Story
                            </span>
                        )}
                    </div>
                ) : (
                    <SectorPlaceholder sectorKey={e.sector_key} name={e.name} />
                )}
            </div>

            <div className="mt-5 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold leading-snug" data-testid={`enterprise-name-${e.slug}`}>
                            {e.name}
                        </h3>
                        <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-[color:var(--gsf-body)]">
                            <Icon size={12} strokeWidth={1.8} className="text-[color:var(--gsf-green-600)]" />
                            <span data-testid={`enterprise-sector-${e.slug}`}>{e.sector}</span>
                        </div>
                    </div>
                    <span
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)] font-semibold uppercase tracking-widest"
                        data-testid={`enterprise-status-${e.slug}`}
                    >
                        <CheckCircle2 size={10} /> {e.status || "Active"}
                    </span>
                </div>

                {e.summary && <p className="mt-3 text-sm text-[color:var(--gsf-body)] leading-relaxed">{e.summary}</p>}

                {(e.featured && e.project_slug) && (
                    <Link
                        to={`/impact-hub/${e.project_slug}`}
                        className="mt-5 gsf-btn-primary text-sm w-fit"
                        data-testid={`enterprise-success-cta-${e.slug}`}
                    >
                        View Success Story <ArrowRight size={14} />
                    </Link>
                )}
            </div>
        </article>
    );
}

export default function EnterprisePortfolio() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/public/enterprises")
            .then(({ data }) => setItems(data.items || []))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="enterprise-portfolio-page">
            <PageHero title="Community Enterprise Portfolio" subtitle="Community Enterprise Development" />

            <section className="gsf-section" data-testid="portfolio-intro">
                <div className="gsf-container max-w-4xl">
                    <div className="gsf-eyebrow">Introduction</div>
                    <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                        Investing in community-owned enterprises that create sustainable livelihoods.
                    </h2>
                    <div className="mt-6 space-y-5 text-[color:var(--gsf-body)] leading-relaxed">
                        <p>
                            Global Seed Foundation invests in community-owned enterprises that create sustainable
                            livelihoods for vulnerable households. Rather than providing one-time assistance, the
                            Foundation supports organised community groups to establish income-generating businesses
                            that strengthen families and build resilient local economies.
                        </p>
                        <p>
                            One funding initiative supported <strong>ten community enterprise groups</strong>{" "}
                            operating across different sectors. These enterprises continue to contribute to household
                            incomes and demonstrate the potential of community-led entrepreneurship.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="portfolio-grid-section">
                <div className="gsf-container">
                    <div className="flex items-end justify-between flex-wrap gap-3">
                        <div>
                            <div className="gsf-eyebrow">Portfolio</div>
                            <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-[color:var(--gsf-green-700)] font-bold">
                                Ten community enterprise groups
                            </h2>
                        </div>
                        <p className="text-xs text-[color:var(--gsf-body)] max-w-sm">
                            Photographs will replace sector illustrations as authentic images are uploaded through the admin panel.
                        </p>
                    </div>

                    {loading ? (
                        <p className="mt-10 text-gray-500 text-sm">Loading portfolio…</p>
                    ) : (
                        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="portfolio-grid">
                            {items.map((e) => <EnterpriseCard key={e.slug} e={e} />)}
                        </div>
                    )}
                </div>
            </section>

            <section className="gsf-section" data-testid="portfolio-sectors">
                <div className="gsf-container">
                    <div className="max-w-3xl">
                        <div className="gsf-eyebrow">Enterprise Sectors</div>
                        <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-[color:var(--gsf-green-700)] font-bold">
                            The types of enterprises supported.
                        </h2>
                        <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">
                            The Foundation supports a diverse range of community enterprise sectors, chosen with
                            community participation to match local skills, markets and opportunities.
                        </p>
                    </div>
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="sector-grid">
                        {SECTORS.map((s) => {
                            const meta = SECTOR_META[s.key];
                            const Icon = meta.icon;
                            return (
                                <div
                                    key={s.key}
                                    className={`rounded-2xl p-5 bg-gradient-to-br ${meta.accent} ring-1 ${meta.ring}`}
                                    data-testid={`sector-${s.key}`}
                                >
                                    <div className="w-11 h-11 rounded-xl bg-white/70 flex items-center justify-center text-[color:var(--gsf-green-700)]">
                                        <Icon size={20} strokeWidth={1.7} />
                                    </div>
                                    <div className="mt-3 text-xs font-medium text-[color:var(--gsf-green-700)]">{s.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-[color:var(--gsf-green-900)] text-white gsf-section" data-testid="portfolio-impact">
                <div className="gsf-container grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">Community Impact</div>
                        <h2 className="mt-3 font-serif text-3xl font-bold">
                            Sustainable income. Household resilience.
                        </h2>
                    </div>
                    <div className="md:col-span-7 space-y-4 text-white/85 leading-relaxed">
                        <p>
                            These enterprises were established to create sustainable income opportunities for vulnerable
                            households and to strengthen community resilience through entrepreneurship. Ownership and
                            leadership sit with the community members themselves — the Foundation's role is to fund,
                            mentor, connect and stand alongside.
                        </p>
                        <p>
                            Financial, production and employment statistics will be published on individual enterprise
                            pages as each group's records are verified. The Foundation does not publish unverified
                            performance figures.
                        </p>
                    </div>
                </div>
            </section>

            <section className="gsf-section" data-testid="portfolio-future">
                <div className="gsf-container max-w-4xl">
                    <div className="gsf-eyebrow">Future Expansion</div>
                    <h2 className="mt-3 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">
                        Each enterprise will have its own dedicated page.
                    </h2>
                    <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">
                        The Foundation is designing individual enterprise pages containing background, funding received,
                        objectives, beneficiaries, photo gallery, progress updates, products and services, testimonials
                        and growth milestones. Administrators can add photographs and updates over time — the website
                        design will remain unchanged and enterprise records will replace placeholders automatically as
                        content becomes available.
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 text-xs text-gray-500">
                        <Camera size={14} /> Placeholders will be replaced with authentic uploaded imagery.
                    </div>
                </div>
            </section>
        </div>
    );
}
